import { forwardRef } from 'react';
import type { ButtonHTMLAttributes, MouseEvent, ReactNode } from 'react';
import { cx } from '../../lib/cx';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger';
export type ButtonSize = 'sm' | 'md';

/**
 * Why a control is currently unavailable, and what to do when someone tries it anyway.
 *
 * Supplying this puts the button on the "explains itself" path (see the "Delete a client"
 * and "Admin Users — table and controls" sections): it
 * is marked `aria-disabled` and styled as unavailable, but it is **not** natively
 * `disabled`, so it stays focusable, hoverable and activatable. Activating it calls
 * `onExplain` and never the button's own `onClick` — the action itself cannot happen.
 *
 * A natively `disabled` button is the wrong tool whenever there is something to say:
 * browsers deliver no mouse events to a disabled form control, so a `title` on one is
 * never seen and the admin is left with a "not allowed" cursor and no explanation.
 */
export interface UnavailableReason {
  /** One sentence, shown as a `title` and repeated by whatever `onExplain` opens. */
  reason: string;
  /** Called instead of `onClick` when the button is clicked or keyed. */
  onExplain: () => void;
}

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  /**
   * Present only while the control is unavailable *and* there is a reason worth telling
   * the reader. Leave it undefined and use the plain `disabled` attribute for controls
   * with nothing to explain — a submit button mid-request, for instance.
   */
  unavailable?: UnavailableReason | undefined;
  icon?: ReactNode;
}

interface VariantStyle {
  /** The live button: background, text and border, all of them, in one string. */
  live: string;
  /**
   * The same three, for a button that is *not* going to act — whether it is
   * `aria-disabled` with an explanation behind it, or natively disabled because its own
   * request is still running.
   */
  quiet: string;
}

/**
 * Each variant is one complete, exclusive set of colour utilities — never a live set with
 * a quieter one layered over it.
 *
 * Layering is the defect this file used to ship: `bg-danger` and `bg-danger/40` on the
 * same element are decided by whichever Tailwind happens to emit later, not by the order
 * they appear in the class attribute. It emitted `bg-danger` later, so **Delete on an
 * active client rendered at full strength** — indistinguishable from an armed Delete,
 * while its `border-transparent` (which did win) removed the outline. The spec's "shown
 * visibly subdued" (see the "Delete a client" and "Admin Users — table and controls"
 * sections) was simply not on screen. Same trap, same shape, as
 * the one documented in `Field.tsx`; the cure is the same: choose, never stack.
 *
 * The quiet look is a real, readable control rather than a wash of the fill colour: a
 * subdued Delete still reads red (it is still the destructive one), but unfilled, so the
 * armed/blocked distinction survives — and its label clears 4.5:1 instead of the 1.85:1
 * that white-on-`bg-danger/40` measured in the light theme.
 */
const VARIANTS: Record<ButtonVariant, VariantStyle> = {
  primary: {
    live: 'bg-primary text-ink-inverted border-primary hover:bg-primary-hover',
    quiet: 'bg-surface-sunken text-ink-muted border-hairline-strong',
  },
  secondary: {
    live: 'bg-surface text-ink border-hairline-strong hover:bg-surface-sunken',
    quiet: 'bg-surface-sunken text-ink-muted border-hairline-strong',
  },
  ghost: {
    live: 'bg-transparent text-ink-muted border-transparent hover:bg-surface-sunken',
    quiet: 'bg-transparent text-ink-subtle border-transparent',
  },
  danger: {
    live: 'bg-danger text-ink-inverted border-danger hover:bg-danger-hover',
    quiet: 'bg-surface-sunken text-danger border-hairline-strong',
  },
};

const SIZES: Record<ButtonSize, string> = {
  sm: 'h-7 px-2.5 text-[12px]',
  md: 'h-8 px-3 text-[13px]',
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = 'secondary',
    size = 'md',
    className,
    unavailable,
    icon,
    children,
    type,
    disabled,
    onClick,
    ...rest
  },
  ref,
) {
  const explains = unavailable !== undefined;
  const styles = VARIANTS[variant];

  // The two paths are mutually exclusive by construction: `unavailable` never sets the
  // native attribute, and without it nothing here touches `aria-disabled`. A caller that
  // passes both gets the explaining path, because a silently refusing control is the one
  // behavior this component exists to prevent.
  function handleClick(event: MouseEvent<HTMLButtonElement>) {
    if (unavailable) {
      event.preventDefault();
      event.stopPropagation();
      unavailable.onExplain();
      return;
    }
    onClick?.(event);
  }

  return (
    <button
      ref={ref}
      {...rest}
      type={type ?? 'button'}
      disabled={explains ? undefined : disabled}
      aria-disabled={explains ? true : undefined}
      title={explains ? unavailable.reason : rest.title}
      onClick={handleClick}
      aria-busy={!explains && disabled ? true : undefined}
      className={cx(
        'inline-flex items-center justify-center gap-1.5 rounded border font-medium',
        'transition-colors',
        // One set or the other, never both — see VARIANTS.
        explains || disabled ? styles.quiet : styles.live,
        // The cursor has to agree with what pressing actually does. A button that
        // explains itself DOES respond, so it keeps the ordinary pointer; saying "not
        // allowed" under the cursor and then opening a dialog on click contradicts
        // itself, and the reader believes the cursor. Only a genuinely disabled control —
        // one whose action is already running — refuses, and says so.
        explains ? 'cursor-pointer' : disabled ? 'cursor-not-allowed' : undefined,
        SIZES[size],
        className,
      )}
    >
      {icon}
      {children}
    </button>
  );
});
